import type { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';

export type FeeMethod = typeof FEE_METHOD[keyof typeof FEE_METHOD];
export type ExpenseArrangement = typeof EXPENSE_ARRANGEMENT[keyof typeof EXPENSE_ARRANGEMENT];

/**
 * Backend enum values sent over the wire.
 * The store maps these to the friendlier frontend aliases above.
 */
export type ApiFeeCalculationMethod = 'Gross' | 'Net' | 'gross' | 'net';
export type ApiExpenseArrangement =
  | 'FirmAdvances'
  | 'ClientPays'
  | 'FirmAbsorbs'
  | 'firmAdvances'
  | 'clientPays'
  | 'firmAbsorbs';
export type ApiSettlementStatus =
  | 'Draft'
  | 'StatementGenerating'
  | 'StatementGenerated'
  | 'Invoiced'
  | 'PaidLocked'
  | 'Reset'
  | 'draft'
  | 'statementGenerating'
  | 'statementGenerated'
  | 'invoiced'
  | 'paidLocked'
  | 'reset'
  | null;
export type ApiStepStatus =
  | 'NotStarted'
  | 'Completed'
  | 'Skipped'
  | 'Invalidated'
  | 'notStarted'
  | 'completed'
  | 'skipped'
  | 'invalidated';
/** SC-45815 Summary value confidence — see SettlementValueStatus (backend). */
export type ApiSettlementValueStatus = 'Estimated' | 'Confirmed' | 'estimated' | 'confirmed';

/** Shape returned by GET/POST/PATCH /v1/matters/{matterId}/settlement */
export interface ApiSettlementResponse {
  settlementId: string;
  matterId: string;
  status: ApiSettlementStatus;
  currentStep: number;
  contingencyFeePercentage: number | null;
  feeCalculationMethod: ApiFeeCalculationMethod | null;
  expenseArrangement: ApiExpenseArrangement | null;
  roundFeeToNearestDollar: boolean;
  feeChargeId: string | null;
  existingSettlementFeeAmount: number | null;
  /** ID of the interest expense created at Step 2 confirm. */
  interestExpenseId?: string | null;
  statementBlobPath: string | null;
  previewStatementBlobPath: string | null;
  completedAt: string | null;
  canReset: boolean;
  resetBlockedReason: string | null;
  /** Non-null when push to QBO is blocked — shown inline in the panel. */
  disbursementBlockedReason?: string | null;
  /** External IDs frozen at Step 1 confirm. Absent on older responses, empty before the first
   *  confirm and after Unlock & Reset — all three mean "no saved selection". */
  selectedDepositIds?: string[] | null;
  /** ISO date string for the deposits filter start date (DateOnly from backend), yyyy-MM-dd. */
  depositsFromDate?: string | null;
  /** ISO date string for the expenses filter start date (DateOnly from backend) — also the
   *  as-of date for expense interest calculation, yyyy-MM-dd. */
  expenseFromDate?: string | null;
  /** ISO datetime string of the settlement date on the matter. */
  settlementDate?: string | null;
  /** Firm-level expense interest rate (e.g. 10 for 10%) — null when NoExpenseInterest or arrangement != FirmAdvances. */
  companyExpenseInterestRate?: number | null;
  /** Whether expense interest is disabled for this matter. */
  noExpenseInterest?: boolean;
  /** The interest rate frozen when Step 2 was confirmed (e.g. 10 for 10%; stored on settlement entity). Null until confirmed. */
  expenseInterestRateUsed?: number | null;
  steps: ApiSettlementStep[];
  summary: ApiSettlementSummary;
  /** Non-null when the settlement invoice has been finalized (V2-1.1). */
  settlementInvoiceId: string | null;
  /** "Created" | "Paid" | null — invoice lifecycle status from backend. */
  invoiceStatus: string | null;
  /** True when the invoice can be finalized now (server-computed). */
  canFinalizeInvoice: boolean;
  /** True when the invoice can be undone now (server-computed). */
  canUndoInvoice: boolean;
  /** Non-null when finalize/undo is blocked — shown inline in the panel. */
  invoiceBlockedReason: string | null;
  /** Non-null when a trust disbursement payment has been applied (V2-1.4). */
  trustPaymentId: string | null;
  /** True when trust payment has been applied (backend explicit flag). */
  trustPaymentApplied?: boolean;
  /** Non-null when the client net proceeds have been pushed to QBO (V2-1.5). */
  qboDisbursementId: string | null;
  /** True when the client disbursement has been pushed (backend explicit flag). */
  clientDisbursementPushed?: boolean;
  /** True when client disbursement can be pushed now (backend explicit permission). */
  canPushClientDisbursement?: boolean;
  /** True when pushed client disbursement can be undone now (backend explicit permission). */
  canUndoClientDisbursement?: boolean;
  /** Non-null when undo client disbursement is blocked (sc-46091: e.g. QBO Cleared/Reconciled). */
  undoDisbursementBlockedReason?: string | null;
  /** The invoice total — amount to be paid from trust (V2-1.1). */
  invoiceAmount: number | null;
  /** The current invoice balance returned after finalize (preferred display value). */
  invoiceBalance?: number | null;
  /** The current trust balance returned by the settlement endpoint, when available. */
  trustBalance?: number | null;
  /** True when trust payment can be created now (server-computed permission). */
  canPayFromTrust?: boolean;
  /** True when trust payment can be undone now (server-computed permission). */
  canUndoTrustPayment?: boolean;
  /** Non-null when creating the trust payment is blocked — shown inline in panel. */
  trustPaymentBlockedReason?: string | null;
  /** Non-null when undoing the trust payment is blocked (sc-46091: e.g. QBO Cleared/Reconciled). */
  undoTrustPaymentBlockedReason?: string | null;
  /** Non-fatal warnings from the last action (e.g. stale statement after finalize/undo). */
  warnings?: string[] | null;
}

export interface ApiSettlementStep {
  stepNumber: number;
  status: ApiStepStatus;
  confirmedAt: string | null;
}

// Wizard step numbers — ApiSettlementStep.stepNumber's values — mirroring backend SettlementSteps.cs.
export enum SettlementStep {
  Deposit = 1,
  Expense = 2,
  AttorneyFee = 3,
  Lien = 4,
  ClientPayment = 5,
  Generate = 6,
}

/** One component of a multi-part sales tax — e.g. GST 5%, PST (BC) 7%. Mirrors the backend
 *  TaxComponent captured from QuickBooks; display only, never used for arithmetic. */
export interface SettlementTaxComponent {
  name: string;
  rate: number | null;
  amount: number;
}

/** Row of GET /v1/clients/{clientId}/settlements/blocking — one settlement past Step 1. */
export interface ApiBlockingSettlement {
  settlementId: string;
  matterId: string;
  matterName?: string | null;
  canReset?: boolean;
  resetBlockedReason?: string | null;
}

export interface ApiBlockingSettlementsResponse {
  settlements?: ApiBlockingSettlement[] | null;
}

/** POST /v1/clients/{clientId}/settlements/reset — the settlements it actually cleared. */
export interface ApiClientSettlementsResetResponse {
  settlementIds?: string[] | null;
}

/**
 * A settlement blocking an accounting-mode change, normalised. `billClient` is a
 * client-level setting, so the guard is client-scoped: every sibling matter's
 * in-flight settlement blocks the change, not just the one being viewed.
 */
export interface BlockingSettlement {
  settlementId: string;
  matterId: string;
  matterName: string;
  canReset: boolean;
  resetBlockedReason: string | null;
}

export interface ApiSettlementSummary {
  recoveryAmount: number | null;
  totalExpenses: number | null;
  alreadyPaidByClient: number | null;
  unreimbursedExpenses: number | null;
  totalLiens: number | null;
  feeBase: number | null;
  attorneyFee: number | null;
  netAttorneyFee: number | null;
  clientNetRecovery: number | null;
  calculationVariant: string | null;
  salesTax: number | null;
  /** GST/PST/QST breakdown from the settlement's linked invoice. Absent/empty for single-tax
   *  firms and settlements predating the canadianTax flag — the card then shows the single
   *  lumped Sales Tax row. salesTax stays authoritative for arithmetic. */
  taxComponents?: SettlementTaxComponent[] | null;
  expenseInterest: number | null;
  /** SC-45815: 'Estimated' when the field is a live, unconfirmed Step 1 estimate rather than a
   *  confirmed/frozen value. May be absent or null on older/partial responses — the mapper then
   *  falls back to the corresponding step's completion status instead of assuming "estimated". */
  totalExpensesStatus?: ApiSettlementValueStatus | null;
  totalLiensStatus?: ApiSettlementValueStatus | null;
  attorneyFeeStatus?: ApiSettlementValueStatus | null;
  clientNetRecoveryStatus?: ApiSettlementValueStatus | null;
  /** Co-counsel (P4.5): only meaningful once feeRecipients has been confirmed —
   *  before that, the backend derives this from the matter's currently-SAVED
   *  recipient percentages, not the user's in-progress unsaved edits, so it's
   *  not safe to use for a live per-keystroke preview. */
  generatingFirmRetainedAmount: number | null;
  generatingFirmRetainedPercentage: number | null;
  /** Co-counsel expenses confirmed for Step 2 — added into the sidebar's
   *  "Less: Expenses" line alongside the firm's own totalExpenses. */
  coCounselExpensesTotal: number | null;
}

/** PATCH payload for the backend */
export interface ApiUpdateSettlementRequest {
  contingencyFeePercentage?: number | null;
  feeCalculationMethod?: ApiFeeCalculationMethod | null;
  expenseArrangement?: ApiExpenseArrangement | null;
  roundFeeToNearestDollar?: boolean;
  depositsFromDate?: string | null;
  expenseFromDate?: string | null;
  /** ISO datetime string — backend saves to matter.SettlementDate. */
  settlementDate?: string | null;
}

/** POST payload for the backend — creates a settlement with required settings */
export interface ApiCreateSettlementRequest {
  contingencyFeePercentage?: number | null;
  feeCalculationMethod?: ApiFeeCalculationMethod | null;
  expenseArrangement?: ApiExpenseArrangement | null;
  roundFeeToNearestDollar?: boolean;
  expenseFromDate?: string | null;
  settlementDate?: string | null;
}

/** API shape from GET /matters/{id}/settlement/deposits */
export interface DepositItem {
  depositId: string;
  amount: number;
  receivedDate: string;
  receivedFrom: string;
  description: string;
  /** BR-V4-8: another matter's settlement already claims this deposit. */
  isLocked?: boolean;
  lockedByMatterName?: string | null;
}

export interface DepositsResponse {
  deposits: DepositItem[];
  totalAvailable: number;
}

/** Payload for POST /settlements/{id}/deposit/confirm */
export interface ConfirmDepositPayload {
  selectedDeposits: ConfirmDepositPayloadItem[];
  /** ISO date string (yyyy-MM-dd) — filters deposits shown in Step 1 (DateOnly on backend). */
  depositsFromDate?: string | null;
}

export interface ConfirmDepositPayloadItem {
  externalDepositId: string;
  amount: number;
  depositDate: string | null;
  description: string;
  sourceType: string;
}

export interface DepositsState {
  items: DepositItem[];
  selectedIds: string[];
  totalAvailable: number;
  loading: boolean;
  error: unknown;
  /** Active date filter (yyyy-MM-dd) the list was last loaded with; null = unset. */
  filterStartDate: string | null;
  filterEndDate: string | null;
}

export interface Lien {
  externalLienTransactionId: string;
  sourceType: string;
  payeeName: string;
  description: string;
  amount: number;
  transactionDate: string;
  validationStatus: string;
}

export interface ConfirmLienItem {
  externalLienTransactionId: string;
  payeeName: string;
  description: string;
  amount: number;
  transactionDate: string;
  sourceType: string;
}

export interface ConfirmLiensRequest {
  selectedLiens: ConfirmLienItem[];
}

// ── Step 4 FF: From-Table Lien Types (liens flipper ON) ──

/** Single lien row within a grouped lien response from /from-table */
export interface FromTableLien {
  lienId: string;
  payeeName: string;
  description: string | null;
  amount: number;
  status: string;
  lienType: string;
}

/** A group of liens by type, returned by the from-table endpoint */
export interface FromTableLienGroup {
  lienType: string | null;
  displayName: string;
  liens: FromTableLien[];
  subtotal: number;
}

/** Response shape from GET /settlements/{id}/liens/from-table */
export interface FromTableLiensResponse {
  lienGroups: FromTableLienGroup[];
  totalLiens: number;
  hasUnfinalizedLiens: boolean;
  /** Whether the firm is connected to QuickBooks Online. */
  isQboConnected?: boolean;
}

export interface LiensState {
  items: Lien[];
  totalLiens: number;
  loading: boolean;
  error: unknown;
}

export interface ComputedBreakdown {
  adjustedRecovery: number | null;
  fee: number | null;
  netAttorneyFee: number | null;
  clientNet: number | null;
  unreimbursedExpenses: number | null;
  lienTotal: number | null;
  totalExpenses: number | null;
  alreadyPaidByClient: number | null;
  calculationVariant: string | null;
  salesTax: number | null;
  /** See ApiSettlementSummary.taxComponents. */
  taxComponents: SettlementTaxComponent[];
  expenseInterest: number | null;
  /** Co-counsel expenses confirmed for Step 2 — see ApiSettlementSummary. */
  coCounselExpensesTotal: number | null;
  /** Co-counsel (P4.5): the generating firm's own share of the fee. Drives the firm row's
   *  Fee Amount / Total in MatterFeeRecipientsTab — see ApiSettlementSummary for the caveat
   *  about it lagging unsaved recipient edits. */
  generatingFirmRetainedAmount: number | null;
  /** SC-45815: true when the field is a live, unconfirmed Step 1 estimate rather than a confirmed/frozen value. */
  isExpensesConfirmed: boolean;
  isLiensConfirmed: boolean;
  isAttorneyFeeConfirmed: boolean;
  isClientNetRecoveryConfirmed: boolean;
}

/**
 * Internal frontend settlement shape used by store and components.
 * The store maps the ApiSettlementResponse into this shape.
 */
export interface SettlementResponse {
  settlementId: string | null;
  matterId: string;
  status: ApiSettlementStatus;
  matterName: string;
  clientName: string;
  feePercentage: number | null;
  feeMethod: FeeMethod | null;
  expenseArrangement: ExpenseArrangement | null;
  recoveryAmount: number | null;
  selectedDepositIds: string[];
  roundToNearestDollar: boolean;
  computed: ComputedBreakdown;
  expenses: unknown[];
  deposits: DepositItem[];
  liens: Lien[];
  steps: ApiSettlementStep[];
  feeChargeId: string | null;
  existingSettlementFeeAmount: number | null;
  /** Non-null when the PDF has been generated — the blob storage path for download. */
  blobPath: string | null;
  /** Non-null when a preview PDF has been generated — the blob storage path for preview download. */
  previewBlobPath: string | null;
  completedAt: string | null;
  /** Whether the settlement can be reset (Unlock & Reset). */
  canReset: boolean;
  /** Reason the reset is blocked (shown as tooltip when canReset is false). */
  resetBlockedReason: string | null;
  locked?: {
    expenseTotal?: number;
  };
  createDate: string;
  modifiedDate: string;
  /** Non-null when the settlement invoice has been finalized. */
  settlementInvoiceId: string | null;
  /** "Created" | "Paid" | null — invoice lifecycle status from backend. */
  invoiceStatus: string | null;
  /** True when the invoice can be finalized now (server-computed). */
  canFinalizeInvoice: boolean;
  /** True when the invoice can be undone now (server-computed). */
  canUndoInvoice: boolean;
  /** Non-null when finalize/undo is blocked — shown inline in the panel. */
  invoiceBlockedReason: string | null;
  /** Non-null when a trust disbursement payment has been applied. */
  trustPaymentId: string | null;
  /** True when trust payment has been applied (backend explicit flag). */
  trustPaymentApplied?: boolean;
  /** Non-null when the client net proceeds have been pushed to QBO. */
  qboDisbursementId: string | null;
  /** True when the client disbursement has been pushed (server-computed). */
  clientDisbursementPushed: boolean;
  /** True when client disbursement can be pushed now (server-computed). */
  canPushClientDisbursement: boolean;
  /** True when pushed client disbursement can be undone now (server-computed). */
  canUndoClientDisbursement: boolean;
  /** Non-null when undo client disbursement is blocked (sc-46091: e.g. QBO Cleared/Reconciled). */
  undoDisbursementBlockedReason?: string | null;
  /** The invoice total — amount to pay from trust. */
  invoiceAmount: number | null;
  /** The invoice balance returned after finalize. */
  invoiceBalance?: number | null;
  /** The current trust balance returned with the settlement payload. */
  trustBalance?: number | null;
  /** True when trust payment can be created now (server-computed permission). */
  canPayFromTrust?: boolean;
  /** True when trust payment can be undone now (server-computed permission). */
  canUndoTrustPayment: boolean;
  /** Non-null when trust payment create/undo is blocked — shown inline in panel. */
  trustPaymentBlockedReason: string | null;
  /** Non-null when undo trust payment is blocked (workflow order, or sc-46091 QBO Cleared/Reconciled). */
  undoTrustPaymentBlockedReason?: string | null;
  /** Non-null when push to QBO is blocked — shown inline in the panel. */
  disbursementBlockedReason?: string | null;
  /** ISO date string for the deposits filter start (DateOnly from backend). */
  depositsFromDate?: string | null;
  /** ISO date string for the expenses filter start (DateOnly from backend). */
  expenseFromDate?: string | null;
  /** ISO datetime of the settlement date on the matter (null if not set). */
  settlementDate?: string | null;
  /** Firm expense interest rate — null when NoExpenseInterest or arrangement != FirmAdvances. */
  companyExpenseInterestRate?: number | null;
  /** Whether expense interest is disabled for this matter (from matter.BillingInfo). */
  noExpenseInterest?: boolean;
  /** The interest rate used when Step 2 was last confirmed. */
  expenseInterestRateUsed?: number | null;
  /** ID of the soft-cost interest expense created at Step 2 confirmation. */
  interestExpenseId?: string | null;
  /** Non-fatal warnings from the last action (e.g. stale statement after finalize/undo). */
  warnings?: string[] | null;
}

/** Store state slice for post-settlement actions panel (V2-FE-1). */
export interface PostSettlementState {
  saving: boolean;
  error: string | null;
  trustBalance: number | null;
  trustBalanceLoading: boolean;
  trustBalanceError: boolean;
}

export interface StatementState {
  /** True while polling GET /settlement waiting for blobPath to become non-null. */
  polling: boolean;
  /** True while POST /statement/generate request is in-flight. */
  generating: boolean;
  /** True when polling timed out (30s) before blobPath was found. */
  timedOut: boolean;
  /** ISO timestamp of when the statement was generated (set locally when polling succeeds). */
  generatedAt: string | null;
  /** Authenticated blob object URL for PDF viewing/downloading (created via downloadStatementBlob). */
  blobUrl: string | null;
  /** True while POST /statement/generate?preview=true request is in-flight or polling for preview. */
  previewGenerating: boolean;
  /** True while polling for previewBlobPath after preview generation. */
  previewPolling: boolean;
  /** True when preview polling timed out. */
  previewTimedOut: boolean;
  /** Authenticated blob object URL for preview PDF viewing (created via downloadPreviewStatementBlob). */
  previewBlobUrl: string | null;
}

/** Internal frontend PATCH shape — the service maps this to ApiUpdateSettlementRequest. */
export interface UpdateSettlementRequest {
  feeMethod?: FeeMethod | null;
  expenseArrangement?: ExpenseArrangement | null;
  roundToNearestDollar?: boolean;
  recoveryAmount?: number | null;
  feePercentage?: number | null;
  lockExpenses?: boolean;
  lockClientNet?: boolean;
  /** ISO datetime string — saves to matter.SettlementDate via backend. */
  settlementDate?: string | null;
  /** yyyy-MM-dd — interest as-of date; persisted so a Settings-panel edit survives reload. */
  expenseFromDate?: string | null;
}

export interface EditorLockDto {
  lockedByAccountId: string;
  lockedByName: string | null;
  lockedAt: string;
  expiresAt: string;
}

export interface SettlementStatementNarrativeDto {
  settlementStatementNarrativeId: string;
  settlementId: string;
  enableOpeningParagraph: boolean;
  openingParagraphHtml: string | null;
  enableClosingParagraph: boolean;
  closingParagraphHtml: string | null;
  jurisdictionNote: string | null;
  isCustomized: boolean;
  resolvedOpeningParagraphHtml: string | null;
  resolvedClosingParagraphHtml: string | null;
  modifiedDate: string;
  recordUpdatedById: string | null;
  editorLock: EditorLockDto | null;
}

export interface NarrativeState {
  enableOpeningParagraph: boolean;
  openingParagraphHtml: string | null;
  enableClosingParagraph: boolean;
  closingParagraphHtml: string | null;
  jurisdictionNote: string | null;
  isCustomized: boolean;
  isStale: boolean;
  loading: boolean;
  saving: boolean;
  editorLock: EditorLockDto | null;
  updatedAt: string | null;
}

export interface SettlementStoreState {
  loading: boolean;
  saving: boolean;
  data: SettlementResponse | null;
  settlementId: string | null;
  error: unknown;
  deposits: DepositsState;
  liens: LiensState;
  fromTableLiens: FromTableLiensResponse | null;
  attorneyFee: AttorneyFeeState;
  statement: StatementState;
  payments: PaymentsState;
  postSettlement: PostSettlementState;
  narrative: NarrativeState;
  /** Settlement date from the matter (yyyy-MM-dd). Populated by MatterSettlement after load. */
  settlementDate: string | null;
  /** Company-level expense interest rate (%). Populated by MatterSettlement after load. */
  companyExpenseInterestRate: number | null;
}

export interface SettlementSettings {
  feeMethod: FeeMethod | null;
  expenseArrangement: ExpenseArrangement | null;
  feePercentage: number | null;
  roundToNearestDollar: boolean;
  expenseFromDate?: string | null;
  settlementDate?: string | null;
}

// ── Step 2: Expenses ──────────────────────────────────────

/** Shape of a single expense from GET /v1/expenses */
export interface MatterExpenseItem {
  id: string;
  amount: number;
  description: string;
  /** May be null — expenses without a date are excluded from interest (no-date warning). */
  date: string | null;
  /** Vendor object as returned by the /expenses API — same shape as the rest of the app. */
  vendor?: { id?: string; name?: string; displayName?: string } | null;
  isExpenseInterest?: boolean;
  /** Present when the expense originated from QuickBooks (e.g. hard cost / bill sync). */
  accounting?: { type?: string; href?: string; [key: string]: unknown } | null;
}

/** Response from GET /v1/expenses?matterId=... */
export interface MatterExpenseListResponse {
  expenses: MatterExpenseItem[];
  total: number;
}

export interface ExpensesState {
  items: MatterExpenseItem[];
  totalExpenses: number;
  /** ClientPays only: previously billed case costs, outside this batch (sc-46645). 0 otherwise. */
  billedExpensesTotal: number;
  /** ClientPays only: the itemized expenses backing billedExpensesTotal, for the Status column. Empty otherwise. */
  billedItems: MatterExpenseItem[];
  loading: boolean;
  error: unknown;
  isConfirmed: boolean;
  interestConfirmed: boolean;
  expenseFromDate: string | null;
  confirmedExpenseFromDate: string | null;
  confirmedTotalInterest: number;
  /** Column currently sorted by (redesign only) — persisted so non-sort reloads keep it. */
  orderby: string;
  /** Sort direction currently active (redesign only). */
  order: 'asc' | 'desc';
  /** Bumped on every loadExpenses() call so a stale in-flight response can be detected and ignored. */
  requestToken: number;
}

export interface ConfirmExpensesRequest {
  totalExpenses: number;
  expenses: { expenseId: string; amount: number; description: string }[];
  /** ISO date string (yyyy-MM-dd) — the as-of date for the expense period (DateOnly on backend). */
  expenseFromDate?: string | null;
  /** Calculated interest amount to create as a soft-cost expense (null = no interest). */
  totalInterestAmount?: number | null;
  /** The rate that was applied to calculate the interest (stored on settlement for audit). */
  interestRateUsed?: number | null;
  /** All co-counsel/referring-attorney rows, keyed by matter recipient id
   *  (not vendorId). `role` is omitted for the default 'coCounsel' role and
   *  present for any other role (e.g. 'referringAttorney'). Omitted entirely
   *  when there are no rows at all. */
  coCounselExpenses?: { recipientId: string; amount: number; role?: FeeRecipientRole }[];
}

export interface ConfirmExpenseInterestRequest {
  expenseFromDate: string;
  totalInterestAmount: number;
  interestRateUsed?: number | null;
}

/** Co-Counsel & Referring Attorney Fees — role on a fee recipient. */
export type FeeRecipientRole = 'coCounsel' | 'referringAttorney' | 'other';

/** One entry in GET /settlements/{id}/attorney-fee's feeRecipients[] — the confirmed
 *  settlement-level snapshot. calculatedShareAmount/disbursement fields are null/false
 *  until Step 3 has actually been confirmed for this settlement. */
export interface ApiFeeRecipientData {
  matterSettlementRecipientId: string;
  vendorId: string;
  vendorDisplayName: string;
  role: FeeRecipientRole;
  percentage: number;
  calculatedShareAmount: number | null;
  disbursementPushed: boolean;
  disbursementQboId: string | null;
}

/** Shape returned by GET /settlements/{id}/attorney-fee */
export interface ApiAttorneyFeeResponse {
  calculatedAttorneyFee: number;
  existingFees: ApiFeeEntry[];
  hasMismatch: boolean;
  settlementFeeChargeId: string | null;
  summary: ApiSettlementSummary;
  feeRecipients?: ApiFeeRecipientData[];
}

/** Display-shaped fee recipient used by the Step 3 UI — id is the matter recipient's
 *  own id (MatterSettlementRecipientId), matching the wire field the confirm payload
 *  keys off (FeeRecipientConfirmRequest.recipientId). */
export interface FeeRecipient {
  id: string;
  vendorId: string;
  vendorDisplayName: string;
  role: FeeRecipientRole;
  percentage: number;
  calculatedShareAmount: number | null;
  disbursementPushed: boolean;
  disbursementQboId: string | null;
}

/** Narrower wire shape for POST /attorney-fee/confirm's feeRecipients[] — vendorId/role
 *  are display-only and ignored by the backend, which resolves them from recipientId. */
export interface FeeRecipientConfirmRequest {
  recipientId: string;
  percentage: number;
}

/** One entry in GET /matters/{matterId}/settlement/recipients' recipients[] — the
 *  matter-level defaults used to auto-populate Step 3 before it's been confirmed. */
export interface MatterRecipientData {
  matterSettlementRecipientId: string;
  vendorId: string;
  vendorDisplayName: string;
  role: FeeRecipientRole;
  percentage: number;
  sortOrder: number;
  status: 'Submitted' | 'Pending' | null;
  feeAmount: number | null;
  expenses: number | null;
  total: number | null;
  /** Set only once locked — the id the disbursements submit/undo endpoints require
   *  for this recipient's FeeRecipient row (distinct from matterSettlementRecipientId). */
  settlementFeeRecipientId: string | null;
}

/** Shape returned by GET /matters/{matterId}/settlement/recipients */
export interface MatterRecipientsResponse {
  isLocked: boolean;
  recipients: MatterRecipientData[];
}

export interface CreateMatterRecipientRequest {
  vendorId: string;
  role: FeeRecipientRole;
  percentage: number;
  sortOrder: number;
}

/** `vendorId` never actually changes on update, but the backend validates it against the
 *  request body (not the stored entity) — omitting it causes a false 400. Always send it. */
export interface UpdateMatterRecipientRequest {
  vendorId: string;
  role: FeeRecipientRole;
  percentage: number;
  sortOrder: number;
}

/** Raw wire shape of one entry in GET /settlements/{id}/expenses/co-counsel's
 *  coCounselExpenses[] — one row per matter recipient, joined with any confirmed
 *  Step 2 co-counsel amount (defaulting to 0 when none exists yet). Money is
 *  decimal dollars, matching every other wire field on this feature. */
export interface ApiCoCounselExpenseData {
  matterSettlementRecipientId: string;
  vendorId: string;
  vendorDisplayName: string;
  role: FeeRecipientRole;
  amount: number;
}

/** Display-shaped co-counsel expense row used by Step 2 — id is the matter
 *  recipient's own id (matterSettlementRecipientId), matching the wire field
 *  the confirm payload keys off (coCounselExpenses[].recipientId). */
export interface CoCounselExpense {
  id: string;
  vendorId: string;
  vendorDisplayName: string;
  role: FeeRecipientRole;
  amount: number;
}

/** Shape returned by GET /settlements/{settlementId}/expenses/co-counsel */
export interface CoCounselExpensesResponse {
  coCounselExpenses: ApiCoCounselExpenseData[];
  summary: { coCounselExpensesTotal: number };
}

export interface ApiFeeEntry {
  chargeId: string;
  description: string;
  amount: number;
  feeDate: string | null;
  timekeeperId: string | null;
  isSettlementFee: boolean;
  status: string;
}

/** POST payload for /settlements/{id}/attorney-fee/record */
export interface RecordFeeRequest {
  amount: number;
  description?: string;
  feeDate?: string;
  timekeeperId?: string;
}

/**
 * POST payload for /settlements/{id}/attorney-fee/confirm.
 *
 * Legacy (settlementRedesign off): feeChargeId is required — the charge must
 * already be recorded via /attorney-fee/record before confirming.
 *
 * Redesign (settlementRedesign on): feeChargeId is optional. Omit it to have
 * the backend create a new charge; provide it to update/link an existing one.
 * feeDescription/feeDate feed directly into that single call. The backend
 * always uses its own AttorneyFeeSnapshot for the amount — never client-supplied.
 */
export interface ConfirmAttorneyFeePayload {
  feeChargeId?: string;
  feeDescription?: string;
  feeDate?: string;
  /** recipientId + percentage only — vendorId/role are display-only and resolved
   *  server-side from recipientId; never send the full FeeRecipient display shape. */
  feeRecipients?: FeeRecipientConfirmRequest[];
}

export interface AttorneyFeeState {
  calculatedFee: number | null;
  existingFees: ApiFeeEntry[];
  hasMismatch: boolean;
  settlementFeeChargeId: string | null;
  selectedChargeId: string | null;
  summary: ApiSettlementSummary | null;
  loading: boolean;
  recording: boolean;
  /** Confirmed settlement-level snapshot (post Step-3-confirm) once non-empty, or
   *  matter-level defaults auto-populated pre-confirm — see loadMatterFeeRecipients. */
  feeRecipients: FeeRecipient[];
  /** True once GET /matters/{matterId}/settlement/recipients has resolved with an
   *  empty list — distinguishes "not fetched yet" from "fetched and genuinely empty". */
  noMatterRecipientsConfigured: boolean;
  recipientsLoading: boolean;
}

// ── Step 5: Client Payments ────────────────────────────

/** Invoice reference attached to a payment (timeSheetId + number). */
export interface PaymentInvoiceReference {
  timeSheetId: string;
  invoiceNumber: string | null;
  /** Matters the invoice spans, alphabetical. Absent for single-matter invoices. */
  matterNames?: string[];
}

/** A payment item — used for both live payments and frozen selections. */
export interface ClientPaymentItem {
  externalPaymentId: string;
  externalSourceType: string;
  paymentDate: string | null;
  amount: number;
  method: string | null;
  memo: string | null;
  isIncluded: boolean;
  invoices: PaymentInvoiceReference[];
  /** BR-V4-11: another matter's settlement for this client already credits this payment. */
  isLocked?: boolean;
  /** Matter holding the claim — names the settlement to edit in the DD-V4-10 hover. */
  lockedByMatterName?: string | null;
}

/** Matters a Step 5 payment spans, split for the "Matters:" column. */
export interface MatterNameSummary {
  all: string[];
  visible: string[];
  hiddenCount: number;
}

/** Display-ready payment item returned by the displayPayments getter. */
export interface DisplayPaymentItem {
  externalPaymentId: string;
  paymentDate: string | null;
  amount: number;
  method: string | null;
  memo: string | null;
  invoices: PaymentInvoiceReference[];
  isIncluded: boolean;
  isLocked: boolean;
  lockedByMatterName: string | null;
}

/** Response from GET /settlements/{id}/client-payments */
export interface ClientPaymentsApiResponse {
  payments: ClientPaymentItem[];
  totalPayments: number;
  frozenSelections: ClientPaymentItem[];
  selectedPaymentIds: string[];
  summary: ApiSettlementSummary;
}

/** Store state for the payments sub-slice */
export interface PaymentsState {
  items: ClientPaymentItem[];
  selectedIds: string[];
  frozenSelections: ClientPaymentItem[];
  loading: boolean;
  error: unknown;
}

/** Payload item for POST /settlements/{id}/client-payments/confirm */
export interface ConfirmClientPaymentItem {
  externalPaymentId: string;
  amount: number;
  paymentDate: string | null;
  method: string | null;
  memo: string | null;
  sourceType: string;
  isIncluded: boolean;
}

/** Payload for POST /settlements/{id}/client-payments/confirm */
export interface ConfirmClientPaymentsPayload {
  allPayments: ConfirmClientPaymentItem[];
}

/**
 * Response from GET /settlement-statement-template/keywords
 *
 * NOTE: All fields (id, key, name) are backend-controlled.
 * Frontend uses these values only for display purposes via Vue text interpolation.
 * Vue automatically escapes HTML in text interpolation, making these values safe for display.
 */
export interface SettlementKeywordInfo {
  /** Backend-generated unique identifier for the keyword */
  id: string;
  /** Keyword token format: {keyword_name} */
  key: string;
  /** Display name for the keyword — backend-controlled, Vue-escaped for safety */
  name: string;
  /**
   * Resolved per-matter value. Returned ONLY when the request is scoped to a
   * settlement (matter page). Absent on the firm template page, where the
   * frontend supplies sample values instead.
   */
  value?: string;
}

/**
 * Enriched keyword with local metadata (sample values, grouping).
 * Created by enrichBackendKeywordsWithMetadata() combining backend keywords with frontend definitions.
 */
export interface EnrichedSettlementKeyword {
  name: string;
  token: string;
  description: string;
  sampleValue: string;
  group: string;
}

/**
 * Response from GET /settlement-statement-template
 *
 * NOTE: HTML content fields (openingParagraphHtml, closingParagraphHtml)
 * are backend-sanitized but should be re-sanitized on frontend before rendering.
 * The UpdateSettlementStatementTemplatePage component applies frontend sanitization
 * to enforce defense-in-depth (allowed tags: p, strong, em, ul, li, br only).
 */
export interface SettlementStatementTemplateDto {
  settlementStatementTemplateId: string;
  enableOpeningParagraph: boolean;
  /**
   * HTML content for opening paragraph. May contain p, strong, em, ul, li, br tags only.
   * Must be sanitized before rendering via v-html or similar unsafe methods.
   */
  openingParagraphHtml: string | null;
  enableClosingParagraph: boolean;
  /**
   * HTML content for closing paragraph. May contain p, strong, em, ul, li, br tags only.
   * Must be sanitized before rendering via v-html or similar unsafe methods.
   */
  closingParagraphHtml: string | null;
  /** Jurisdiction note — plain text only, safe to render directly */
  jurisdictionNote: string | null;
  createdAt?: string;
  modifiedDate?: string;
}

/**
 * Request payload for POST /settlement-statement-template
 *
 * NOTE: HTML fields must be sanitized before submission.
 * Frontend (UpdateSettlementStatementTemplatePage) sanitizes content before sending.
 * Allowed HTML tags: p, strong, em, ul, li, br only. All attributes are stripped.
 */
export interface NarrativeContentInput {
  enableOpeningParagraph: boolean;
  /** Sanitized HTML (p, strong, em, ul, li, br tags only, no attributes) */
  openingParagraphHtml: string | null;
  enableClosingParagraph: boolean;
  /** Sanitized HTML (p, strong, em, ul, li, br tags only, no attributes) */
  closingParagraphHtml: string | null;
  /** Plain text only */
  jurisdictionNote: string | null;
}

// ── Settlement Statement Keywords / Preview ─────────────────────────────

export interface SettlementKeywordDefinition {
  token: string;
  sampleValue: string;
  group?: string;
}

export interface SettlementPreviewRow {
  label: string;
  valueKey: string;
  bold?: boolean;
}

export interface SettlementPreviewMeta {
  statementDate: string;
  rows: SettlementPreviewRow[];
}
